/**
 * Is anything actually coming down the line?
 *
 * An agent connected to a call has three ways to hear nothing, and from the
 * chair they are identical — the timer runs, the panel says "Active Call", and
 * there is silence:
 *
 *  1. Nobody is there. A dead line, a carrier that answered on the merchant's
 *     behalf, a voicemail that has not started its greeting. Audio is arriving;
 *     it just carries no sound. This is the common one and it is not a fault.
 *  2. No media is reaching this browser at all. ICE picked a path that does not
 *     carry, the connection dropped without the call ending. This IS a fault
 *     and today it is invisible — nothing distinguishes it from (1).
 *  3. Audio is arriving and playing, and the agent's own output device is
 *     muted, unplugged, or set to the wrong headset.
 *
 * The browser can tell these apart, so the agent should not have to guess. The
 * inbound-rtp report on the peer connection carries both halves of the answer:
 * `packetsReceived` says whether media is arriving, and `audioLevel` says
 * whether that media carries sound. One says "our fault", the other says
 * "nobody home", and the difference is the whole point of this file.
 *
 * The logic is pure and lives apart from the polling so it can be tested
 * without a peer connection. `sampleInboundAudio` is the only part that touches
 * WebRTC, and it never throws — a browser that reports nothing must leave the
 * agent no worse off than before this existed.
 *
 * What this deliberately does NOT do is detect voicemail. A greeting is sound
 * like any other, so it reads as `live`. Voicemail is AMD's job; this answers
 * the narrower question AMD cannot: is there any audio at all.
 */

/**
 * `warming-up` is the honest answer for the first seconds of a call — media
 * takes a moment to flow and a badge that cries "silent" on every connect
 * would be ignored by the second day.
 */
export type RemoteAudioState = 'warming-up' | 'live' | 'silent' | 'no-media';

export interface RemoteAudioReading {
  state: RemoteAudioState;
  /** Last inbound level the browser reported, 0..1. */
  level: number;
  /** Milliseconds since sound last arrived. Counts from the call start when none ever has. */
  quietForMs: number;
}

/** One poll of the inbound audio report. Every field is optional evidence. */
export interface InboundAudioSample {
  at: number;
  /** Cumulative inbound packets, or null when the browser publishes no such report. */
  packetsReceived: number | null;
  /** Instantaneous level 0..1, when the browser reports one. */
  audioLevel: number | null;
  /** Cumulative energy — the fallback for browsers that omit `audioLevel`. */
  totalAudioEnergy: number | null;
}

export interface RemoteAudioMemo {
  startedAt: number;
  /**
   * Whether any poll has returned anything at all.
   *
   * The load-bearing field. Without it, a browser or SDK version that hands
   * back no inbound report looks exactly like a line with no sound on it, and
   * every call would raise a silence warning that is really about us. No
   * evidence means no verdict — say nothing rather than the wrong thing.
   */
  observed: boolean;
  /** Null until a packet count has been seen at all. */
  lastPackets: number | null;
  /** When that count last went up. Seeded to the start so a frozen path still ages. */
  packetsGrewAt: number;
  /** Null until sound has been heard on this call. */
  heardAt: number | null;
  level: number;
  totalAudioEnergy: number | null;
}

/**
 * Speech sits far above this; comfort noise and a silent carrier sit below it.
 * Set low on purpose — a merchant three feet from a speakerphone is quiet, and
 * calling a real person "silent" is the expensive mistake here.
 */
export const SPEECH_LEVEL = 0.02;
/** Energy is cumulative, so any real growth counts. Guards float noise only. */
const ENERGY_EPSILON = 1e-6;
/** Nothing is judged before this. Media has to arrive first. */
export const WARM_UP_MS = 2000;
/** Silence worth telling the agent about. Shorter than any natural pause. */
export const QUIET_MS = 5000;
/** A packet count that has stopped moving for this long means the path is dead. */
export const NO_MEDIA_MS = 3000;

export function createRemoteAudioMemo(startedAt: number): RemoteAudioMemo {
  return {
    startedAt,
    observed: false,
    lastPackets: null,
    packetsGrewAt: startedAt,
    heardAt: null,
    level: 0,
    totalAudioEnergy: null,
  };
}

/** Fold one poll into the running picture. */
export function foldInboundAudio(
  memo: RemoteAudioMemo,
  sample: InboundAudioSample
): RemoteAudioMemo {
  const packets = sample.packetsReceived;
  const packetsGrewAt =
    packets !== null && (memo.lastPackets === null || packets > memo.lastPackets)
      ? sample.at
      : memo.packetsGrewAt;

  // Two ways to hear sound, because browsers differ on which they publish.
  // `audioLevel` is instantaneous; `totalAudioEnergy` only ever grows, so any
  // growth between two polls means sound arrived in that window.
  const level = sample.audioLevel ?? 0;
  const energyGrew =
    sample.totalAudioEnergy !== null &&
    memo.totalAudioEnergy !== null &&
    sample.totalAudioEnergy - memo.totalAudioEnergy > ENERGY_EPSILON;
  const heard = level >= SPEECH_LEVEL || energyGrew;

  return {
    startedAt: memo.startedAt,
    observed:
      memo.observed ||
      packets !== null ||
      sample.audioLevel !== null ||
      sample.totalAudioEnergy !== null,
    lastPackets: packets ?? memo.lastPackets,
    packetsGrewAt,
    heardAt: heard ? sample.at : memo.heardAt,
    level,
    totalAudioEnergy: sample.totalAudioEnergy ?? memo.totalAudioEnergy,
  };
}

/**
 * Read the picture as something to put on screen.
 *
 * `no-media` is claimed only when a packet count was seen and then stopped
 * moving. A browser that publishes no inbound report at all is not evidence of
 * a broken path — it is evidence of a browser we cannot ask — so that case
 * falls through to the level, which is the conservative answer.
 */
export function readRemoteAudio(memo: RemoteAudioMemo, now: number): RemoteAudioReading {
  const sinceStart = Math.max(0, now - memo.startedAt);
  const quietForMs = memo.heardAt === null ? sinceStart : Math.max(0, now - memo.heardAt);
  const level = memo.level;

  // Nothing has ever answered us, so there is nothing to report. This is the
  // difference between "the line is silent" and "we cannot hear the line".
  if (!memo.observed) return { state: 'warming-up', level, quietForMs };

  if (
    memo.lastPackets !== null &&
    sinceStart >= WARM_UP_MS &&
    now - memo.packetsGrewAt >= NO_MEDIA_MS
  ) {
    return { state: 'no-media', level, quietForMs };
  }

  if (quietForMs >= QUIET_MS) {
    return { state: 'silent', level, quietForMs };
  }

  // Heard something recently — or too early to say either way.
  return { state: memo.heardAt === null ? 'warming-up' : 'live', level, quietForMs };
}

/**
 * Read the inbound audio report off a peer connection.
 *
 * Never throws and never rejects: this runs on a timer during a live call, and
 * a stats API that misbehaves must not take the call panel down with it. An
 * unreadable poll returns all-null, which every rule above treats as "no new
 * evidence" rather than as bad news.
 */
export async function sampleInboundAudio(
  pc: RTCPeerConnection | null | undefined,
  now: number = Date.now()
): Promise<InboundAudioSample> {
  const empty: InboundAudioSample = {
    at: now,
    packetsReceived: null,
    audioLevel: null,
    totalAudioEnergy: null,
  };
  if (!pc || typeof pc.getStats !== 'function') return empty;

  try {
    const report = await pc.getStats();
    let sample = empty;
    report.forEach((entry: any) => {
      if (entry?.type !== 'inbound-rtp') return;
      // `kind` is the modern field; `mediaType` is what older Chrome reports.
      const kind = entry.kind ?? entry.mediaType;
      if (kind !== 'audio') return;
      sample = {
        at: now,
        packetsReceived:
          typeof entry.packetsReceived === 'number' ? entry.packetsReceived : null,
        audioLevel: typeof entry.audioLevel === 'number' ? entry.audioLevel : null,
        totalAudioEnergy:
          typeof entry.totalAudioEnergy === 'number' ? entry.totalAudioEnergy : null,
      };
    });
    return sample;
  } catch {
    return empty;
  }
}

/** What the whole call turned out to be, for reporting rather than for display. */
export type CallAudioVerdict = 'live' | 'silent' | 'no-media';

/**
 * One verdict for a finished call, to be filed against it.
 *
 * `readRemoteAudio` answers "what is happening right now" and is a badge. This
 * answers "what happened on this call" and is evidence, so it is built from the
 * whole memo rather than the last poll: a call that carried sound for four
 * minutes and lost media in its final second is a call that worked, and the
 * point-in-time reading would file it as a fault.
 *
 * `null` is a real answer and the most important one. Agents report calls where
 * they heard nothing; without a way to say "we cannot tell", every such call
 * would be filed as evidence about the line when it is really evidence about
 * the browser. So no verdict is written when nothing ever answered our polls,
 * and none when the call ended before media had time to arrive.
 *
 * The three verdicts map to the three silences this file exists to separate:
 * `live` means audio arrived and carried sound; `silent` means it arrived and
 * carried none, which is a dead line and not our fault; `no-media` means it
 * stopped arriving, which is.
 */
export function summariseCallAudio(
  memo: RemoteAudioMemo,
  endedAt: number
): CallAudioVerdict | null {
  // Nothing ever answered us. Saying "silent" here would be a claim about the
  // call made from a fact about the browser.
  if (!memo.observed) return null;

  // Sound arrived at some point, so the path worked, whatever came later.
  if (memo.heardAt !== null) return 'live';

  // Too short to have expected media yet — no verdict rather than a wrong one.
  if (endedAt - memo.startedAt < WARM_UP_MS) return null;

  // Same bar as `readRemoteAudio`: only a packet count that was seen and then
  // stopped moving is evidence of a broken path. A browser that publishes no
  // count is one we cannot ask, and falls through to the conservative answer.
  if (memo.lastPackets !== null && endedAt - memo.packetsGrewAt >= NO_MEDIA_MS) {
    return 'no-media';
  }
  return 'silent';
}
